/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type InjectedHttpError = 400 | 403 | 404 | 500 | 502 | 503 | null;

/** Forced lifecycle every `DataState` surface is pushed into, or `'off'` for real props. */
export type SimulateDataState = 'off' | 'loading' | 'error' | 'empty';

export interface StateSimulatorState {
  perpetualLoading: boolean;
  networkBroken: boolean;
  injectedHttpError: InjectedHttpError;
  injectedErrorCount: number;
  latencyMs: number;
  hoverInspector: boolean;
  simulateRecaptcha401: boolean;
  simulateDataState: SimulateDataState;
  screenshotMode: boolean;

  setPerpetualLoading: (v: boolean) => void;
  setNetworkBroken: (v: boolean) => void;
  setInjectedHttpError: (code: InjectedHttpError, count: number) => void;
  consumeInjectedError: () => void;
  setLatencyMs: (ms: number) => void;
  setHoverInspector: (v: boolean) => void;
  setSimulateRecaptcha401: (v: boolean) => void;
  setSimulateDataState: (v: SimulateDataState) => void;
  setScreenshotMode: (v: boolean) => void;
  resetAll: () => void;
}

const INITIAL: Pick<StateSimulatorState, 'perpetualLoading' | 'networkBroken' | 'injectedHttpError' | 'injectedErrorCount' | 'latencyMs' | 'hoverInspector' | 'simulateRecaptcha401' | 'simulateDataState' | 'screenshotMode'> = {
  perpetualLoading: false,
  networkBroken: false,
  injectedHttpError: null,
  injectedErrorCount: 0,
  latencyMs: 0,
  hoverInspector: false,
  simulateRecaptcha401: false,
  simulateDataState: 'off',
  screenshotMode: false,
};

export const STORAGE_KEY = 'DEVCOCKPIT_SIM';

/**
 * Zustand store for toggling simulated app states (loading, network errors, etc.).
 * Persisted to localStorage so simulations survive page refresh.
 */
export const useStateSimulatorStore = create<StateSimulatorState>()(
  persist(
    (set) => ({
      ...INITIAL,

      setPerpetualLoading: (v) => set({ perpetualLoading: v }),
      setNetworkBroken: (v) => set({ networkBroken: v }),
      setInjectedHttpError: (code, count) => set({ injectedHttpError: code, injectedErrorCount: count }),
      consumeInjectedError: () =>
        set((s) => {
          const next = s.injectedErrorCount - 1;
          return next <= 0
            ? { injectedErrorCount: 0, injectedHttpError: null }
            : { injectedErrorCount: next };
        }),
      setLatencyMs: (ms) => set({ latencyMs: ms }),
      setHoverInspector: (v) => set({ hoverInspector: v }),
      setSimulateRecaptcha401: (v) => set({ simulateRecaptcha401: v }),
      setSimulateDataState: (v) => set({ simulateDataState: v }),
      setScreenshotMode: (v) => set({ screenshotMode: v }),
      resetAll: () => set(INITIAL),
    }),
    {
      name: STORAGE_KEY,
      version: 1,
      partialize: (state) => ({
        perpetualLoading: state.perpetualLoading,
        networkBroken: state.networkBroken,
        injectedHttpError: state.injectedHttpError,
        latencyMs: state.latencyMs,
        hoverInspector: state.hoverInspector,
        simulateRecaptcha401: state.simulateRecaptcha401,
        simulateDataState: state.simulateDataState,
        screenshotMode: state.screenshotMode,
      }),
    },
  ),
);
